import { DEFAULT_SIGNAL, parseTag } from "./addressing";
import type { CalcField, CalcOp, ChannelAddress, SignalType } from "./types";

const CALC_OPS = new Set<CalcOp>(["avg", "sum", "min", "max", "add", "sub", "mul", "div"]);
const SIGNALS = new Set<SignalType>(["v", "acc", "s"]);

export function getMachineCalcStorageKey(workspaceId: string, machineId: string): string {
  return `ghm:machine-calcs:${workspaceId}:${machineId}`;
}

export function normalizeCalcFields(value: unknown, machineId: string): CalcField[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((candidate): CalcField[] => {
    if (!candidate || typeof candidate !== "object") return [];
    const item = candidate as Record<string, unknown>;
    const op = item.op;
    const derivedIndex = item.derivedIndex;
    const label = item.label;
    const inputs = item.inputs;

    if (typeof op !== "string" || !CALC_OPS.has(op as CalcOp)) return [];
    if (!Number.isInteger(derivedIndex) || Number(derivedIndex) < 0) return [];
    if (!Array.isArray(inputs)) return [];

    const channelInputs = inputs.flatMap((input): ChannelAddress[] => {
      if (!input || typeof input !== "object") return [];
      const obj = input as Record<string, unknown>;
      const slotIndex = Number(obj.slotIndex);
      const portIndex = Number(obj.portIndex);
      if (!Number.isInteger(slotIndex) || slotIndex < 0) return [];
      if (!Number.isInteger(portIndex) || portIndex < 0) return [];

      // Migracao suave: campos calculados salvos antes da introducao do sinal
      // explicito (`v`/`acc`/`s`) defaultam para `DEFAULT_SIGNAL` (acc) — a
      // semantica anterior, que sempre lia acc.
      const rawSignal = obj.signal;
      const signal: SignalType =
        typeof rawSignal === "string" && SIGNALS.has(rawSignal as SignalType)
          ? (rawSignal as SignalType)
          : DEFAULT_SIGNAL;

      return [{ kind: "real", slotIndex, portIndex, signal }];
    });

    if (channelInputs.length === 0) return [];

    return [
      {
        id: typeof item.id === "string" && item.id ? item.id : `calc-${derivedIndex}`,
        machineId,
        label: typeof label === "string" && label.trim() ? label.trim() : `Calculo V${derivedIndex}`,
        op: op as CalcOp,
        inputs: channelInputs,
        derivedIndex: Number(derivedIndex),
      },
    ];
  });
}

export function parseCalcInputList(value: string): ChannelAddress[] {
  // Aceita tanto a tag publica nova (`G0_P1_v`, `G0_P1_acc`, `G0_P1_s`) quanto
  // a notacao tecnica antiga (`S0.P1`, sem sinal explicito — defaulta acc).
  return value
    .split(",")
    .map((part) => parseTag(part))
    .filter((address): address is ChannelAddress => !!address);
}

export function loadLocalCalcFields(storage: Pick<Storage, "getItem">, key: string, machineId: string) {
  const raw = storage.getItem(key);
  if (!raw) return [];

  try {
    return normalizeCalcFields(JSON.parse(raw), machineId);
  } catch {
    return [];
  }
}

export function saveLocalCalcFields(storage: Pick<Storage, "setItem">, key: string, fields: CalcField[]) {
  storage.setItem(key, JSON.stringify(fields));
}
